import React, { useState, useCallback, useMemo, useEffect, useRef, memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Copy, Check, Download } from 'lucide-react';

// ── Copy-to-clipboard: entire message ─────────────────────────
const MessageCopyButton = ({ content, dark = false }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    // Strip special tokens (DB tables, wireframe refs) — copy plain text only
    const plain = content
      .replace(/\[DB_TABLE\][\s\S]*?\[\/DB_TABLE\]/g, '[table]')
      .replace(/\[WIREFRAME_VIEW:[^\]]+\]/g, '[wireframe]')
      .replace(/\[WIREFRAME_IMG:[^\]]+\]/g, '[wireframe]');
    navigator.clipboard.writeText(plain).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const baseClass = dark
    ? 'opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-md hover:bg-white/20 text-white/60 hover:text-white shrink-0 self-start mt-0.5'
    : 'opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-md hover:bg-slate-200 text-slate-500 hover:text-slate-700 shrink-0 self-start mt-0.5';

  return (
    <button
      onClick={handleCopy}
      className={baseClass}
      title="Copy message"
      aria-label="Copy message"
    >
      {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
    </button>
  );
};

// ── Helper: build CSV string and trigger download ──
function downloadTableAsCsv(columns, rows, filename = 'query_result.csv') {
  const escape = (v) => {
    const s = v == null ? '' : String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const lines = [columns.map(escape).join(',')];
  for (const row of rows) lines.push(row.map(escape).join(','));
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const CopyButton = ({ code }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button
      onClick={handleCopy}
      /* Slightly larger tap target on mobile */
      className="absolute top-2 right-2 p-2 rounded bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white transition-colors touch-manipulation"
      title="Copy code"
    >
      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
    </button>
  );
};

const ChatMessage = memo(({ content, role, onImageClick }) => {
  const markdownComponents = useMemo(() => ({
    // ── Code blocks ────────────────────────────────────────────────
    code({ node, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      const codeString = String(children).replace(/\n$/, '');
      const isBlockCode = !!(match || codeString.includes('\n'));

      if (isBlockCode) {
        return (
          <div
            className="relative my-3 rounded-md"
            style={{ border: '1px solid #2d3748' }}
          >
            {/* Language label */}
            <div className="flex items-center justify-between px-3 py-1.5 bg-slate-800 text-slate-400 text-xs font-mono rounded-t-md">
              <span>{match ? match[1] : 'code'}</span>
            </div>
            <CopyButton code={codeString} />
            {/*
              Key mobile fix: max-w-0 + overflow-x-auto keeps the block
              from blowing past the viewport. The inner min-width:max-content
              lets long lines scroll instead of wrapping or getting clipped.
            */}
            <div
              className="overflow-x-auto w-full"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              <SyntaxHighlighter
                style={oneDark}
                language={match ? match[1] : undefined}
                PreTag="div"
                customStyle={{
                  margin: 0,
                  borderRadius: 0,
                  /* 0.75rem is readable but compact on small screens */
                  fontSize: 'clamp(0.7rem, 2.5vw, 0.8rem)',
                  whiteSpace: 'pre',
                  minWidth: 'max-content',
                }}
                codeTagProps={{ style: { whiteSpace: 'pre', display: 'block' } }}
                {...props}
              >
                {codeString}
              </SyntaxHighlighter>
            </div>
          </div>
        );
      }

      // Inline code
      return (
        <code
          className="px-1 py-0.5 rounded bg-slate-200 text-slate-800 font-mono break-all"
          style={{ fontSize: 'clamp(0.7rem, 2.5vw, 0.8rem)' }}
          {...props}
        >
          {children}
        </code>
      );
    },

    // ── Headings ──────────────────────────────────────────────────
    h1: ({ children }) => (
      <h1 className="text-base sm:text-lg font-bold text-slate-900 mt-4 mb-2 pb-1 border-b border-slate-200">
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-sm sm:text-base font-bold text-slate-900 mt-4 mb-2 pb-1 border-b border-slate-200">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-sm font-semibold text-slate-900 mt-3 mb-1">{children}</h3>
    ),

    // ── Horizontal rule ──────────────────────────────────────────
    hr: () => <hr className="my-3 border-slate-300" />,

    // ── Lists ────────────────────────────────────────────────────
    ul: ({ children }) => (
      <ul className="list-disc list-outside pl-4 sm:pl-5 space-y-1 my-2">{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className="list-decimal list-outside pl-4 sm:pl-5 space-y-1 my-2">{children}</ol>
    ),
    li: ({ children }) => (
      <li className="text-slate-800 leading-relaxed text-sm sm:text-base">{children}</li>
    ),

    // ── Paragraphs ───────────────────────────────────────────────
    p: ({ children }) => (
      <p className="text-slate-800 leading-relaxed my-1 text-sm sm:text-base">{children}</p>
    ),

    // ── Bold / Italic ────────────────────────────────────────────
    strong: ({ children }) => (
      <strong className="font-semibold text-slate-900">{children}</strong>
    ),
    em: ({ children }) => (
      <em className="italic text-slate-700">{children}</em>
    ),

    // ── Blockquote ───────────────────────────────────────────────
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-[#F87217] pl-3 my-2 text-slate-600 italic bg-orange-50 py-1 pr-2 rounded-r text-sm sm:text-base">
        {children}
      </blockquote>
    ),

    // ── Tables ───────────────────────────────────────────────────
    /*
      Mobile fix: wrap in overflow-x-auto so wide tables scroll
      horizontally rather than overflowing the viewport.
    */
    table: ({ children }) => (
      <div
        className="overflow-x-auto my-3 -mx-1"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <table className="min-w-full text-xs sm:text-sm border border-slate-200 rounded">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => (
      <thead className="bg-slate-100 text-slate-700 font-semibold">{children}</thead>
    ),
    tbody: ({ children }) => <tbody>{children}</tbody>,
    tr: ({ children }) => (
      <tr className="border-b border-slate-200 hover:bg-slate-50">{children}</tr>
    ),
    th: ({ children }) => (
      <th className="px-2 sm:px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="px-2 sm:px-3 py-1.5 sm:py-2 text-slate-800">{children}</td>
    ),

    // ── Images (including inline SVG / data: URI wireframes) ─────
    img: ({ src, alt }) => {
      if (!src) return null;
      return (
        <img
          src={src}
          alt={alt || 'Image'}
          className="max-w-full h-auto rounded-lg my-3 block shadow-sm border border-slate-200 cursor-pointer hover:opacity-90 transition-opacity"
          style={{ maxHeight: '640px', width: 'auto' }}
          loading="lazy"
          onClick={() => onImageClick?.(src)}
        />
      );
    },
  }), [onImageClick]);

  // ── Parse segments: DB tables, inline data: URI images, and markdown ──────
  const segments = useMemo(() => {
    if (!content) return [{ type: 'md', text: '' }];

    // Collect all special ranges in one pass
    const ranges = [];

    // 1. [DB_TABLE]...[/DB_TABLE]
    const dbRegex = /\[DB_TABLE\]([\s\S]*?)\[\/DB_TABLE\]/g;
    let m;
    while ((m = dbRegex.exec(content)) !== null) {
      ranges.push({ start: m.index, end: m.index + m[0].length, type: 'table', raw: m[1] });
    }

    // 2. Inline data: URI images — handles normal and whitespace-broken syntax
    //    Matches ![alt](data:image/...) allowing any whitespace between ] and (
    const imgRegex = /!\[([^\]]*)\]\s*\(\s*(data:image\/[^\s)]+)\s*\)/g;
    while ((m = imgRegex.exec(content)) !== null) {
      ranges.push({ start: m.index, end: m.index + m[0].length, type: 'dataimg', alt: m[1], src: m[2] });
    }

    // 3. Wireframe SVG images — custom [WIREFRAME_IMG:alt:base64] token (LEGACY).
    //    Kept for backward compatibility with existing chat history.
    const wireframeRegex = /\[WIREFRAME_IMG:([^:\]]+):([A-Za-z0-9+/=]+)\]/g;
    while ((m = wireframeRegex.exec(content)) !== null) {
      ranges.push({ start: m.index, end: m.index + m[0].length, type: 'wireframe', alt: m[1], base64: m[2] });
    }

    // 4. New HTML wireframes — [WIREFRAME_VIEW:file_id:screen_name] token.
    //    Rendered as a sandboxed iframe that fetches the HTML from the API.
    const wireframeViewRegex = /\[WIREFRAME_VIEW:([^:\]]+):([^\]]+)\]/g;
    while ((m = wireframeViewRegex.exec(content)) !== null) {
      ranges.push({ start: m.index, end: m.index + m[0].length, type: 'wireframeView', fileId: m[1], name: m[2] });
    }

    // Sort by position
    ranges.sort((a, b) => a.start - b.start);

    const parts = [];
    let lastIndex = 0;
    for (const range of ranges) {
      if (range.start > lastIndex) {
        parts.push({ type: 'md', text: content.slice(lastIndex, range.start) });
      }
      if (range.type === 'dataimg') {
        parts.push({ type: 'dataimg', alt: range.alt, src: range.src });
      } else if (range.type === 'wireframe') {
        parts.push({ type: 'wireframe', alt: range.alt, base64: range.base64 });
      } else if (range.type === 'wireframeView') {
        parts.push({ type: 'wireframeView', fileId: range.fileId, name: range.name });
      } else {
        try {
          parts.push({ type: 'table', data: JSON.parse(range.raw) });
        } catch {
          parts.push({ type: 'md', text: content.slice(range.start, range.end) });
        }
      }
      lastIndex = range.end;
    }
    if (lastIndex < content.length) {
      parts.push({ type: 'md', text: content.slice(lastIndex) });
    }
    return parts.length ? parts : [{ type: 'md', text: content }];
  }, [content]);

  if (role === 'user') {
    return (
      <div className="group relative flex items-start gap-1.5 min-w-0 justify-end">
        <MessageCopyButton content={content} dark={true} />
        <p className="text-sm sm:text-base whitespace-pre-wrap text-white break-words flex-1 min-w-0">
          {content}
        </p>
      </div>
    );
  }

  return (
    /*
      overflow-hidden prevents any child from punching through the bubble
      on mobile. min-w-0 is the canonical Flexbox fix that stops text/code
      from overflowing a flex child.
      "group" enables the hover-reveal of the copy button via group-hover.
    */
    <div className="group relative flex items-start gap-1.5 min-w-0">
      <div className="text-sm prose prose-slate max-w-none min-w-0 overflow-hidden flex-1 prose-headings:font-semibold prose-headings:text-slate-900 prose-p:text-slate-800 prose-p:leading-relaxed prose-li:text-slate-800 prose-strong:text-slate-900 prose-hr:border-slate-300">
        {segments.map((seg, idx) =>
          seg.type === 'table' ? (
            <DbTableBlock key={idx} data={seg.data} />
          ) : seg.type === 'dataimg' ? (
            <div
              key={idx}
              className="my-3 rounded-lg border border-slate-200 overflow-hidden shadow-sm"
              style={{ maxHeight: '600px', overflow: 'auto' }}
            >
              <img
                src={seg.src}
                alt={seg.alt || 'Wireframe'}
                className="w-full h-auto cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => onImageClick?.(seg.src)}
                loading="lazy"
              />
            </div>
          ) : seg.type === 'wireframe' ? (
            <div
              key={idx}
              className="my-4 rounded-lg border border-slate-200 overflow-hidden shadow-sm"
              style={{ maxHeight: '600px', overflow: 'auto' }}
            >
              <div className="px-3 py-1.5 bg-slate-100 border-b border-slate-200 text-xs font-medium text-slate-600">
                {seg.alt}
              </div>
              <img
                src={`data:image/svg+xml;base64,${seg.base64}`}
                alt={seg.alt || 'Wireframe'}
                className="w-full h-auto cursor-pointer hover:opacity-90 transition-opacity block"
                onClick={() => onImageClick?.(`data:image/svg+xml;base64,${seg.base64}`)}
                loading="lazy"
              />
            </div>
          ) : seg.type === 'wireframeView' ? (
            <WireframeViewBlock key={idx} fileId={seg.fileId} name={seg.name} />
          ) : (
            <ReactMarkdown
              key={idx}
              components={markdownComponents}
              remarkPlugins={[remarkGfm]}
              urlTransform={(url) => url}
            >
              {seg.text}
            </ReactMarkdown>
          )
        )}
      </div>
      <MessageCopyButton content={content} dark={false} />
    </div>
  );
});

ChatMessage.displayName = 'ChatMessage';

// ── Inline DB table ───────────────────────────────────────────
const PREVIEW_ROWS = 10;

const DbTableBlock = memo(({ data }) => {
  const { columns, rows, total } = data;
  const previewRows = rows.slice(0, PREVIEW_ROWS);

  return (
    <div
      className="overflow-x-auto my-3 border border-slate-200 rounded-lg"
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      <table className="min-w-full text-xs sm:text-sm">
        <thead className="bg-slate-100">
          <tr>
            {columns.map((col, ci) => (
              <th
                key={ci}
                className="px-2 sm:px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider border-b border-slate-200 whitespace-nowrap"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {previewRows.map((row, ri) => (
            <tr
              key={ri}
              className={`border-b border-slate-100 ${ri % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-blue-50 transition-colors`}
            >
              {row.map((cell, ci) => (
                <td key={ci} className="px-2 sm:px-3 py-1.5 sm:py-2 text-slate-800 whitespace-nowrap">
                  {cell != null ? String(cell) : ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Footer: stack vertically on very small screens */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 py-2 bg-slate-50 border-t border-slate-200">
        <span className="text-xs text-slate-500">
          Showing {previewRows.length} of {total} rows
        </span>
        <button
          onClick={() => downloadTableAsCsv(columns, rows)}
          className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[#F87217] hover:bg-[#C65B12] active:bg-[#C65B12] rounded-md transition-colors touch-manipulation w-full sm:w-auto"
        >
          <Download className="w-3.5 h-3.5 shrink-0" />
          Download CSV ({total} rows)
        </button>
      </div>
    </div>
  );
});

DbTableBlock.displayName = 'DbTableBlock';

// ── Wireframe HTML viewer (iframe-based) ──────────────────────
const WIREFRAME_API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const WireframeViewBlock = memo(({ fileId, name }) => {
  const iframeRef = useRef(null);
  const [htmlContent, setHtmlContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const handleDownload = useCallback(() => {
    if (!htmlContent) return;
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${name.replace(/[^a-z0-9_-]/gi, '_')}_wireframe.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [htmlContent, name]);

  useEffect(() => {
    let cancelled = false;
    const fetchHtml = async () => {
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await fetch(`${WIREFRAME_API}/files/download/${fileId}`, { headers });
        if (!res.ok) throw new Error(`Failed to load wireframe (${res.status})`);
        const text = await res.text();
        if (!cancelled) {
          setHtmlContent(text);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message);
          setLoading(false);
        }
      }
    };
    fetchHtml();
    return () => { cancelled = true; };
  }, [fileId]);

  if (loading) {
    return (
      <div className="my-4 rounded-lg border border-slate-200 overflow-hidden shadow-sm">
        <div className="px-3 py-1.5 bg-slate-100 border-b border-slate-200 text-xs font-medium text-slate-600">
          {name}
        </div>
        <div className="h-64 flex items-center justify-center bg-gray-50">
          <div className="animate-pulse text-sm text-gray-400">Loading wireframe...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-4 rounded-lg border border-red-200 overflow-hidden shadow-sm">
        <div className="px-3 py-1.5 bg-red-50 border-b border-red-200 text-xs font-medium text-red-600">
          {name} — Error
        </div>
        <div className="p-4 text-sm text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className={`my-4 rounded-lg border border-slate-200 overflow-hidden shadow-sm ${expanded ? 'fixed inset-4 z-50 flex flex-col bg-white' : ''}`}>
      <div className="px-3 py-1.5 bg-slate-100 border-b border-slate-200 flex items-center justify-between shrink-0">
        <span className="text-xs font-medium text-slate-600 truncate max-w-[60%]">{name}</span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">Interactive Wireframe</span>
          {/* Download button */}
          <button
            onClick={handleDownload}
            disabled={!htmlContent}
            title="Download wireframe as HTML"
            className="flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-slate-200 hover:bg-[#F87217] hover:text-white text-slate-600 transition-colors disabled:opacity-40"
          >
            <Download className="w-3 h-3" />
            <span>Download</span>
          </button>
          {/* Expand / collapse */}
          <button
            onClick={() => setExpanded(v => !v)}
            title={expanded ? 'Collapse' : 'Expand fullscreen'}
            className="px-2 py-0.5 rounded text-xs bg-slate-200 hover:bg-slate-300 text-slate-600 transition-colors"
          >
            {expanded ? '✕ Close' : '⛶ Expand'}
          </button>
        </div>
      </div>
      <iframe
        ref={iframeRef}
        srcDoc={htmlContent}
        title={name}
        sandbox="allow-scripts"
        className="w-full border-0 flex-1"
        style={{ height: expanded ? '100%' : '500px', background: '#f8fafc' }}
      />
    </div>
  );
});

WireframeViewBlock.displayName = 'WireframeViewBlock';

export default ChatMessage;
